import { useTranslation } from "react-i18next";
import { cn } from "~/lib/cn";
import { Button } from "~/components/ui";
import { AppLink } from "~/components/ui/AppLink";
import { LanguageSwitcher } from "~/components/ui/LanguageSwitcher";
import { useConsentStore } from "~/store/consentStore";
import type { NavSessionUser } from "~/lib/nav-session";
import { SOCIAL_PROFILES } from "~/config/social";
import { SocialLink } from "~/components/layout/SocialLink";
import { Wordmark } from "~/components/layout/Wordmark";

interface FooterProps {
  className?: string;
  /** When set (logged-in member), «Iniciar sesión» becomes «Mi espacio». */
  sessionUser?: NavSessionUser | null;
}

function IconSliders() {
  return (
    <svg
      className="ic"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M4 7h9M17 7h3M4 17h3M11 17h9" />
      <circle cx="15" cy="7" r="2" />
      <circle cx="9" cy="17" r="2" />
    </svg>
  );
}

/**
 * Global footer — T98 rebrand, regrouped by T872 S1 to the October delivery and again by
 * the founder on 2026-10-10 (labels that say what she finds behind the click): brand
 * (wordmark · claim · social · ES / EN) · Descubre · Ayuda · Tu espacio, then the legal row.
 * Every destination resolves through `route-config`; the wordmark always leads home.
 */
export function Footer({ className, sessionUser }: FooterProps) {
  const { t } = useTranslation("footer");
  const openConsent = useConsentStore((s) => s.open);
  const year = new Date().getFullYear();
  const publicHome = sessionUser ? "?public=1" : undefined;

  return (
    <footer className={cn("foot mt-auto", className)}>
      <div className="foot-top">
        <div className="foot-grid">
          <div className="foot-brand">
            {/* T634 — a member reaches the real home through `?public=1`; same for «Inicio» below. */}
            <Wordmark routeKey="home" search={publicHome} aria-label={t("home_aria")} className="on-dark" />
            <p className="claim">{t("claim")}</p>
            {/* T735 — real anchors; T872 S1 — one `SocialLink` per network, LinkedIn with
                the filled glyph from the design pass. */}
            <div className="mt-5 flex items-center gap-4">
              <SocialLink network="instagram" href={SOCIAL_PROFILES.instagram} label={t("social.instagram")} />
              <SocialLink network="youtube" href={SOCIAL_PROFILES.youtube} label={t("social.youtube")} />
              <SocialLink network="linkedin" href={SOCIAL_PROFILES.linkedin} label={t("social.linkedin")} />
            </div>
            {/* Beside the brand so the three link groups keep the four-column grid. */}
            <div className="foot-lang">
              <h2>{t("groups.language")}</h2>
              <LanguageSwitcher variant="text" className="on-dark" />
            </div>
          </div>

          <div>
            <h2>{t("groups.discover")}</h2>
            <AppLink routeKey="home" search={publicHome}>{t("discover.home")}</AppLink>
            <AppLink routeKey="metodo">{t("discover.method")}</AppLink>
            <AppLink routeKey="autoTest">{t("discover.self_test")}</AppLink>
            <AppLink routeKey="guias">{t("discover.guides")}</AppLink>
            <AppLink routeKey="diastasis">{t("discover.diastasis")}</AppLink>
          </div>

          <div>
            <h2>{t("groups.help")}</h2>
            <AppLink routeKey="faq">{t("help.faq")}</AppLink>
            <AppLink routeKey="support">{t("help.contact")}</AppLink>
          </div>

          <div>
            <h2>{t("groups.space")}</h2>
            {sessionUser ? (
              <>
                <AppLink routeKey="dashboard">{t("space.my_space")}</AppLink>
                {/* T735 — the footer stays her way back to the library from a public page. */}
                <AppLink routeKey="library">{t("space.library")}</AppLink>
              </>
            ) : (
              <AppLink routeKey="signup">{t("space.sign_in")}</AppLink>
            )}
          </div>
        </div>
      </div>

      <div className="foot-bottom">
        <span>{t("copyright", { year })}</span>
        <nav className="legal" aria-label={t("legal.nav_aria")}>
          <AppLink to="/legal/notice">{t("legal.legal_notice")}</AppLink>
          <AppLink to="/legal/terms">{t("legal.terms")}</AppLink>
          <AppLink to="/legal/privacy">{t("legal.privacy")}</AppLink>
          <AppLink to="/legal/cookies">{t("legal.cookies")}</AppLink>
          <AppLink to="/legal/medical-disclaimer">
            {t("legal.medical_disclaimer")}
          </AppLink>
          <Button type="button" variant="text" className="legal-cookies" onClick={openConsent}>
            <IconSliders />
            {t("legal.cookie_settings")}
          </Button>
        </nav>
      </div>
    </footer>
  );
}
